import { type Directive } from 'vue'

/** 扩展 HTMLImageElement 以存储 observer 引用 */
interface LazyImageElement extends HTMLImageElement {
  __lazyObserver?: IntersectionObserver
}

function loadImage(el: LazyImageElement, src: string): void {
  el.src = src
}

function observeLazyImage(el: LazyImageElement, src: string): void {
  el.dataset['src'] = src
  el.setAttribute('loading', 'lazy')
  el.decoding = 'async'

  if (!('IntersectionObserver' in window)) {
    loadImage(el, src)
    return
  }

  const observer = new IntersectionObserver(
    entries => {
      const entry = entries[0]
      if (entry && entry.isIntersecting) {
        loadImage(el, src)
        observer.unobserve(el)
      }
    },
    { rootMargin: '300px' }
  )
  observer.observe(el)
  el.__lazyObserver = observer
}

/**
 * 图片懒加载指令 v-lazy-img
 * 基于 IntersectionObserver，图片进入视口 300px 范围时开始加载
 * 用法: <img v-lazy-img="imageUrl" :alt="altText" />
 */
export const vLazyImg: Directive<LazyImageElement, string> = {
  mounted(el, binding) {
    if (!binding.value) return
    observeLazyImage(el, binding.value)
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue && binding.value) {
      el.__lazyObserver?.disconnect()
      if (el.src) {
        loadImage(el, binding.value)
      } else {
        observeLazyImage(el, binding.value)
      }
    }
  },
  unmounted(el) {
    el.__lazyObserver?.disconnect()
  },
}
